import { memo, useMemo, useEffect, useState, useCallback } from "react";

import { useProduct } from "@shopify/hydrogen-react";
import { type FilterValue } from "@shopify/hydrogen-react/storefront-api-types";

import { Carousel } from "@components/Carousel";
import { CarouselVisibilityProvider } from "@components/Carousel/CarouselVisibilityContext";
import { InnerArrows } from "@components/Carousel/extensions";
import { ProductCardOption } from "@components/ProductCard/ProductCardOption.data";
import { useCarouselAria } from "@react/a11y/carouselAria";
import { isColorOption } from "@react/utilities";
import { prioritizeColorOptions } from "@react/utilities/collection";
import { extractTaxonomyValueGID } from "@react/utilities/taxonomyColorMapper";
import { formatProduct, formatProductVariant } from "@tablescape/utilities";
import { type TTailwindString, tw } from "classnames";

const SWATCH_GAP = 8;
const SWATCH_WIDTH = {
  mobile: 24,
  desktop: 30,
};
// When any option has a label, use mini (30px) swatches for more label room
const SWATCH_WIDTH_WITH_LABEL = { mobile: 30, desktop: 30 };

// Static carousel width classes (Tailwind JIT requires complete class names at build time).
// These literal widths already include space for swatches, SWATCH_GAP spacing, and
// (for *Arrows variants) the inner arrow controls themselves. If arrow dimensions change, update
// these values directly. Selection is based on hasArrows(swatchCount, perPage).
const CAROUSEL_WIDTH_CLASSES = {
  // Mobile: 4 swatches visible (24px)
  mobileNoArrows: "w-[120px]" as TTailwindString,
  mobileArrows: "w-[156px]" as TTailwindString,
  // md (768px+): 5 swatches visible
  mdNoArrows: "md:w-[182px]" as TTailwindString,
  mdArrows: "md:w-[222px]" as TTailwindString,
  // lg (1280px+): 6 swatches visible
  lgNoArrows: "lg:w-[220px]" as TTailwindString,
  lgArrows: "lg:w-[260px]" as TTailwindString,
  // xl (1440px+): 7 swatches visible
  xlNoArrows: "xl:w-[258px]" as TTailwindString,
  xlArrows: "xl:w-[298px]" as TTailwindString,
} as const;

// When any option has a label, swatches are 30px (mini); mobile widths only differ
const CAROUSEL_WIDTH_CLASSES_WITH_LABEL = {
  mobileNoArrows: "w-[144px]" as TTailwindString,
  mobileArrows: "w-[182px]" as TTailwindString,
  mdNoArrows: CAROUSEL_WIDTH_CLASSES.mdNoArrows,
  mdArrows: CAROUSEL_WIDTH_CLASSES.mdArrows,
  lgNoArrows: CAROUSEL_WIDTH_CLASSES.lgNoArrows,
  lgArrows: CAROUSEL_WIDTH_CLASSES.lgArrows,
  xlNoArrows: CAROUSEL_WIDTH_CLASSES.xlNoArrows,
  xlArrows: CAROUSEL_WIDTH_CLASSES.xlArrows,
} as const;

// Check if arrows are needed based on swatch count and perPage value
const hasArrows = (swatchCount: number, perPage: number): boolean =>
  swatchCount > perPage;

const styles = {
  base: tw("flex", "flex-wrap", "gap-2"),
  carousel: (swatchCount: number, withLabel: boolean) => {
    const widths = withLabel
      ? CAROUSEL_WIDTH_CLASSES_WITH_LABEL
      : CAROUSEL_WIDTH_CLASSES;
    return tw(
      "items-center",
      "overflow-x-auto",
      hasArrows(swatchCount, 4) ? widths.mobileArrows : widths.mobileNoArrows,
      hasArrows(swatchCount, 5) ? widths.mdArrows : widths.mdNoArrows,
      hasArrows(swatchCount, 6) ? widths.lgArrows : widths.lgNoArrows,
      hasArrows(swatchCount, 7) ? widths.xlArrows : widths.xlNoArrows
    );
  },
};

type props = {
  appliedColorFilters?: FilterValue[];
  nextLabel: string;
  prevLabel: string;
  variantMatchCollectionName?: string;
};

export const ProductCardOptionsBase = ({
  appliedColorFilters = [],
  nextLabel,
  prevLabel,
  variantMatchCollectionName,
}: props) => {
  const { product, selectedOptions, setSelectedOption, variants } =
    useProduct();
  const [hasInteractedWithOptions, setHasInteractedWithOptions] =
    useState(false);
  const [visibleIndices, setVisibleIndices] = useState<Set<number>>(new Set());

  const formattedProduct = useMemo(() => formatProduct(product), [product]);
  const formattedVariants = useMemo(
    () =>
      variants.map(formatProductVariant).filter((variant) => !variant.hidden),
    [variants]
  );

  const colorOption = useMemo(
    () => formattedProduct.options?.find((option) => isColorOption(option)),
    [formattedProduct]
  );

  const matchingColorOptionValues = useMemo(() => {
    if (!variantMatchCollectionName || !colorOption) return undefined;

    // Get color option values from variants in the specified collection
    const matchingVariantColorOptionNames = new Set(
      formattedVariants
        .filter((variant) =>
          variant.collectionNames?.includes(variantMatchCollectionName)
        )
        .map(
          (variant) =>
            variant.selectedOptions.find(
              (option) => option.name === colorOption.name
            )?.value
        )
        .filter(Boolean) // Remove undefined values
    );

    // Return color option values that match the collection
    return colorOption.optionValues.filter((optionValue) =>
      matchingVariantColorOptionNames.has(optionValue.name)
    );
  }, [colorOption, variantMatchCollectionName, formattedVariants]);

  const eligibleColorOptionValues = useMemo(() => {
    return matchingColorOptionValues || (colorOption?.optionValues ?? []);
  }, [matchingColorOptionValues, colorOption]);

  const filterGIDsKey = useMemo(() => {
    return appliedColorFilters
      .map(extractTaxonomyValueGID)
      .filter((v): v is string => !!v)
      .sort()
      .join(",");
  }, [appliedColorFilters]);

  const prioritizedColorValues = useMemo(() => {
    if (!colorOption) return [];

    return prioritizeColorOptions(
      eligibleColorOptionValues,
      appliedColorFilters,
      formattedVariants
    );
  }, [
    colorOption,
    eligibleColorOptionValues,
    formattedVariants,
    appliedColorFilters,
  ]);

  const hasAnyOptionLabel = useMemo(() => {
    if (!colorOption) return false;
    return prioritizedColorValues.some((value) =>
      formattedVariants.some(
        (v) =>
          !!v.optionLabel &&
          v.selectedOptions.some(
            (opt) => opt.name === colorOption.name && opt.value === value.name
          )
      )
    );
  }, [colorOption, prioritizedColorValues, formattedVariants]);

  // Force-select if a color based collection name is active
  useEffect(() => {
    if (
      !colorOption ||
      !matchingColorOptionValues ||
      hasInteractedWithOptions ||
      appliedColorFilters.length !== 0
    )
      return;
    if (
      selectedOptions[colorOption.name] !== matchingColorOptionValues[0].name
    ) {
      setSelectedOption(colorOption.name, matchingColorOptionValues[0].name);
    }
  }, [
    colorOption,
    selectedOptions,
    setSelectedOption,
    matchingColorOptionValues,
    hasInteractedWithOptions,
    appliedColorFilters.length,
  ]);

  const carouselKey = useMemo(() => {
    // Create a unique key based on applied filters and prioritized values
    const filterIds = filterGIDsKey;
    const valueNames = prioritizedColorValues.map((v) => v.name).join(",");
    return `${filterIds}-${valueNames}`;
  }, [filterGIDsKey, prioritizedColorValues]);

  // Auto-select first prioritized variant when color filters are applied and order changes
  useEffect(() => {
    if (
      !colorOption ||
      appliedColorFilters.length === 0 ||
      hasInteractedWithOptions
    ) {
      return;
    }

    // Check if prioritization actually changed the order
    const originalFirstOption = colorOption.optionValues[0];
    const prioritizedFirstOption = prioritizedColorValues[0];
    const currentlySelected = selectedOptions[colorOption.name];

    if (
      originalFirstOption?.name !== prioritizedFirstOption?.name &&
      currentlySelected !== prioritizedFirstOption?.name
    ) {
      // Order changed and prioritized value isn't already selected - auto-select it
      setSelectedOption(colorOption.name, prioritizedFirstOption.name);
    }
  }, [
    prioritizedColorValues,
    filterGIDsKey,
    colorOption,
    setSelectedOption,
    selectedOptions,
    hasInteractedWithOptions,
    appliedColorFilters.length,
  ]);

  useEffect(() => {
    setHasInteractedWithOptions(false);
  }, [filterGIDsKey]);

  const handleOptionMouseEnter = useCallback(() => {
    setHasInteractedWithOptions(true);
  }, [setHasInteractedWithOptions]);

  // Track visible swatch indices for accessibility using Intersection Observer
  useEffect(() => {
    const carousel = document.querySelector(
      `[data-carousel-key="${carouselKey}"]`
    );
    if (!carousel) return;

    // Only get swatch slides, not arrow slides
    const swatchSlides = carousel.querySelectorAll(
      ".splide__slide:not(.splide__slide--clone):not([data-arrow])"
    );

    if (swatchSlides.length === 0) return;

    const visibleSet = new Set<number>();

    // Create Intersection Observer for efficient visibility detection
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slide = entry.target as HTMLElement;
          const slideIndex = Array.from(swatchSlides).indexOf(slide);

          if (entry.isIntersecting) {
            visibleSet.add(slideIndex);
          } else {
            visibleSet.delete(slideIndex);
          }
        });

        setVisibleIndices(new Set(visibleSet));
      },
      {
        root: carousel,
        rootMargin: "0px",
        threshold: 0.1, // Trigger when 10% of the slide is visible
      }
    );

    // Observe all swatch slides
    swatchSlides.forEach((slide) => {
      observer.observe(slide);
    });

    const handleCarouselEvent = () => {
      const currentVisibleSet = new Set<number>();
      const activeElement = document.activeElement;

      swatchSlides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const carouselRect = carousel.getBoundingClientRect();
        const isVisible =
          rect.left >= carouselRect.left && rect.right <= carouselRect.right;

        const swatchElement = slide.querySelector('[role="radio"]');
        const swatch =
          swatchElement instanceof HTMLElement ? swatchElement : undefined;

        if (isVisible) {
          slide.removeAttribute("aria-hidden");
          if (slide instanceof HTMLElement) {
            slide.removeAttribute("tabindex");
          }
          if (swatch) {
            swatch.removeAttribute("aria-hidden");
            swatch.tabIndex = 0;
          }
          currentVisibleSet.add(index);
          return;
        }

        slide.setAttribute("aria-hidden", "true");
        if (slide instanceof HTMLElement) {
          slide.setAttribute("tabindex", "-1");
        }
        if (swatch) {
          swatch.setAttribute("aria-hidden", "true");
          swatch.tabIndex = -1;

          if (
            activeElement &&
            (swatch === activeElement || swatch.contains(activeElement))
          ) {
            const nextFocusable = slide.nextElementSibling;
            if (
              nextFocusable instanceof HTMLElement &&
              nextFocusable.tabIndex >= 0
            ) {
              nextFocusable.focus();
            } else {
              const focusableElements = carousel.querySelectorAll(
                '[tabindex]:not([tabindex="-1"])'
              );
              const focusableArray = Array.from(focusableElements).filter(
                (element): element is HTMLElement =>
                  element instanceof HTMLElement
              );
              const currentIndex = focusableArray.indexOf(swatch);
              const nextElement = focusableArray[currentIndex + 1];
              if (nextElement) {
                nextElement.focus();
              } else {
                swatch.blur();
              }
            }
          }
        }
      });

      setVisibleIndices(currentVisibleSet);
    };

    carousel.addEventListener("moved", handleCarouselEvent);
    carousel.addEventListener("resize", handleCarouselEvent);
    carousel.addEventListener("transitioncompleted", handleCarouselEvent);

    return () => {
      // Cleanup Intersection Observer
      observer.disconnect();

      // Remove event listeners with defensive checks
      if (carousel && typeof carousel.removeEventListener === "function") {
        try {
          carousel.removeEventListener("moved", handleCarouselEvent);
          carousel.removeEventListener("resize", handleCarouselEvent);
          carousel.removeEventListener(
            "transitioncompleted",
            handleCarouselEvent
          );
        } catch (error) {
          // Silently handle any cleanup errors to prevent console noise
          console.warn("Error during carousel cleanup:", error);
        }
      }
    };
  }, [carouselKey]);

  const carouselAria = useCarouselAria({
    label: "Product color options",
    totalSlides: prioritizedColorValues.length,
  });

  if (!colorOption) {
    return null;
  }

  return (
    <div
      aria-label={`${colorOption.name} options`}
      className={styles.base}
      role="radiogroup"
    >
      <CarouselVisibilityProvider visibleIndices={visibleIndices}>
        <Carousel
          className={styles.carousel(
            prioritizedColorValues.length,
            hasAnyOptionLabel
          )}
          data-carousel-key={carouselKey}
          extensions={{ InnerArrows: InnerArrows(nextLabel, prevLabel) }}
          key={carouselKey} // Force re-mount when content or filters change
          {...(carouselAria
            ? {
                "aria-label": carouselAria["aria-label"],
                "aria-roledescription": carouselAria["aria-roledescription"],
                "aria-describedby": carouselAria["aria-describedby"],
              }
            : {})}
          options={{
            type: "slide",
            rewind: true,
            clones: 0,
            perPage: 4,
            fixedWidth: hasAnyOptionLabel
              ? SWATCH_WIDTH_WITH_LABEL.mobile
              : SWATCH_WIDTH.mobile,
            gap: SWATCH_GAP,
            pagination: false,
            arrows: false,
            drag: false,
            mediaQuery: "min",
            breakpoints: {
              768: {
                fixedWidth: hasAnyOptionLabel
                  ? SWATCH_WIDTH_WITH_LABEL.desktop
                  : SWATCH_WIDTH.desktop,
                perPage: 5,
              },
              1280: {
                perPage: 6,
              },
              1440: {
                perPage: 7,
              },
            },
          }}
        >
          {prioritizedColorValues.map((value, index) => (
            <ProductCardOption
              index={index}
              key={value.id}
              onMouseEnter={handleOptionMouseEnter}
              optionName={colorOption.name}
              optionValue={value}
              swatchSize={
                hasAnyOptionLabel
                  ? { sm: "mini", md: "mini" }
                  : { sm: "tiny", md: "mini" }
              }
            />
          ))}
        </Carousel>
      </CarouselVisibilityProvider>
    </div>
  );
};

export const ProductCardOptions = memo(ProductCardOptionsBase);
